import "./index.css";

import React from "react";
import ProjectPageComponent from "../ProjectPageComponent";
import {connect} from "react-redux";
import {getPageActions} from "Core/helpers/redux";
import {selectors} from "Core/store/reducers";
import * as appConfig from "../../config";
import * as pageConfig from "./config";
import * as actions from "./actions";
import {getMenuSidebarShrankFromStorage} from "Layout/elements/MainSidebar/helpers";
import {reducerStoreKey} from "./reducer";
import {areAllObjectPropsEmpty, getString, isset, trimArray} from 'Core/helpers/data';
import * as filterDataMap from "./dataMap/filter";
import {scrollToSelector} from "Core/helpers/dom";
import {Tooltip} from "Core/components/global/Tooltip";
import Button, {BUTTON_DISPLAY_TYPE, BUTTON_STYLE} from "Core/components/display/Button";
import {icon_font_create_symbol, icon_font_edit_symbol, icon_font_info_circle_symbol} from 'Config/app';
import Label from "Core/components/display/Label";
import SimpleStaticSearch, {
	SIMPLE_STATIC_SEARCH_DISPLAY_TYPE,
	SIMPLE_STATIC_SEARCH_LAYOUT,
	SimpleStaticSearchOptionObject
} from "Core/components/advanced/SimpleStaticSearch";
import DataTable, {DATA_TABLE_CELL_TYPE} from "Core/components/advanced/DataTable";
import {PAGINATION_TYPE} from "Core/components/action/Pagination";
import {AsyncMountError} from "Core/errors";
import {getCurrentProjectFromUrl, getCurrentProjectIdFromUrl} from 'Helpers/project';
import {
	DataTableCellAnyTypeOptionsDataObject,
	DataTableCellDateTypeOptionsDataObject, DataTableCellTemplateTypeOptionsDataObject
} from 'Core/components/advanced/DataTable/DataTableCell/dataObjects';
import {getAppLocaleDateFormat} from "Core/helpers/locale";
import {LOCALE_DATE_FORMAT_NAME} from "Core/const/locale";
import {
	CAMPAIGN_MESSAGE_CONTENT_TYPE_ICONS,
	CAMPAIGN_MESSAGE_CONTENT_TYPES,
	CAMPAIGN_STATUS,
	CAMPAIGN_STATUSES, CAMPAIGN_TYPE, CAMPAIGN_TYPE_ICONS, MESSAGE_DEFINITION_TYPE
} from './const';
import {STANDARD_DATE_TIME_FORMAT} from "Core/const/datetime";
import CreateItemDialog from "./dialogs/CreateItemDialog";
import CampaignCustomerMessageCount
	from 'Pages/apps/default/projectPages/campaign/components/CampaignCustomerMessageCount';
import Breadcrumbs from 'Core/components/display/Breadcrumbs';
import CampaignWizardDialog from 'Components/dialogs/CampaignWizardDialog';
import {rtrimChar} from 'Core/helpers/string';
import {waitingFunction} from 'Core/helpers/function';
import AdvancedDropdown, {ADVANCED_DROPDOWN_POSITION} from 'Core/components/display/AdvancedDropdown';
import Icon from 'Core/components/display/Icon';
import {find} from 'lodash';
import Acl from 'Acl/projects';

/**
 * Get all actions used by this component
 * @type {Object}
 */
const allActions = getPageActions(actions)

/**
 * Redux 'mapStateToProps' function
 *
 * @param {object} state - Redux entire store state.
 * @return {Object<string, any>} Mapped props that can be used in component.
 */
const mapStateToProps = state => ({
	projectList: selectors.projectSelector.getProjectList(state),
	isMobileBreakpoint: selectors.breakpoint.isMobileBreakpoint(state),
	mainSidebarShrank: getMenuSidebarShrankFromStorage(selectors.mainSidebar.shrank(state)),
	mainList: selectors[reducerStoreKey].getCampaignList(state),
	mainListPagination: selectors[reducerStoreKey].getCampaignListPagination(state),
	mainListSort: selectors[reducerStoreKey].getCampaignListSort(state),
	mainListFilter: selectors[reducerStoreKey].getCampaignListFilter(state),
	singleProject: selectors.projectSelector.getSingleProject(state),
});

class CampaignPage extends ProjectPageComponent {
	constructor(props) {
		super(props, {
			domPrefix: 'campaign-page',
			translationPath: pageConfig.translationPath,
			routerPath: pageConfig.routerPath,
			disableLoad: true,
			optimizedUpdate: true,
			optimizedUpdateIgnoreProps: [...Object.keys(allActions)],
		}, 'page_title');
		
		// Refs
		this.mainListFilterRef = null;
		
		// Initial state
		this.initialState = {
			...super.initialState,
			
			/**
			 * Flag that specifies if main data table height will be limited to the available space
			 */
			limitToAvailableSpace: true,
		};

		// Data methods
		this.reloadMainList = this.reloadMainList.bind(this);
		this.loadMainListPage = this.loadMainListPage.bind(this);
		this.sortMainList = this.sortMainList.bind(this);
		this.filterMainList = this.filterMainList.bind(this);
		this.removeMainListFilter = this.removeMainListFilter.bind(this);
		this.isMainListFilterEmpty = this.isMainListFilterEmpty.bind(this);
		this.reloadMessageCounts = this.reloadMessageCounts.bind(this);

		// Dialog methods
		this.openCreateItemDialog = this.openCreateItemDialog.bind(this);
		this.openCampaignWizardDialog = this.openCampaignWizardDialog.bind(this);

		// Render methods
		this.renderActions = this.renderActions.bind(this);
	}
	
	componentDidUpdate(prevProps, prevState, snapshot) {
		return super.componentDidUpdate(prevProps, prevState, snapshot)
			.then(() => {
				if (prevProps.location.pathname !== this.props.location.pathname) {
					const {resetCampaignAction} = this.props;
					
					// Clear all page data and load the page again
					resetCampaignAction();
					this.loadPageData().catch(() => {});
				}
			});
	}


	// Data methods -----------------------------------------------------------------------------------------------------
	/**
	 * Method that will be called on component mount and should be used to load any data required by the page
	 * @return {Promise<any>}
	 * @throws {AsyncMountError}
	 */
	loadPageData() {
		const {location, mainList, loadCampaignListAction} = this.props;
		const currentProjectIdFromUrl = getCurrentProjectIdFromUrl(location);

		// If main list was already loaded (user already opened the page before)
		if (isset(mainList)) {
			// Open filter if it is not empty
			if (!this.isMainListFilterEmpty() && this.mainListFilterRef) this.mainListFilterRef.open();
			// Reload main list with currently applied filter, sort and pagination
			return this.reloadMainList()
				.then(res => { if (!isset(res)) throw new AsyncMountError(); });
		}
		// Load main list if it is not already loaded
		else {
			return this.executeAbortableActionMount(loadCampaignListAction, currentProjectIdFromUrl)
				.then(() => setTimeout(this.reloadMessageCounts, 100));
		}
	}

	/**
	 * Reload main list using current options (page, sort, ...)
	 * @return {Promise<*>}
	 */
	reloadMainList() {
		const {location, loadCampaignListAction, mainListPagination, mainListSort, mainListFilter} = this.props;
		const currentProjectIdFromUrl = getCurrentProjectIdFromUrl(location);
		const {pageNo, perPage} = mainListPagination;
		const {sortBy, sortDir} = mainListSort;
		const oFilter = filterDataMap.output(mainListFilter);

		return this.executeAbortableAction(
			loadCampaignListAction, currentProjectIdFromUrl, oFilter, pageNo, perPage, sortBy, sortDir
		)
			.then(res => {
				this.reloadMessageCounts();
				this.mainListFilterRef?.reload();
				return res;
			});
	}

	/**
	 * Reload main list using current options (page, sort, ...) if any
	 * @param {number} [pageNo=1] - Page number to load (starts with 1).
	 * @return {Promise<*>}
	 */
	loadMainListPage(pageNo = 1) {
		const {location, loadCampaignListAction, mainListPagination, mainListSort, mainListFilter} = this.props;
		const currentProjectIdFromUrl = getCurrentProjectIdFromUrl(location);
		const {perPage} = mainListPagination;
		const {sortBy, sortDir} = mainListSort;
		const oFilter = filterDataMap.output(mainListFilter);

		return this.executeAbortableAction(
			loadCampaignListAction, currentProjectIdFromUrl, oFilter, pageNo, perPage, sortBy, sortDir
		)
			.then(() => this.reloadMessageCounts());
	}

	/**
	 * Sort main list
	 * @param {string} sortBy - Name of the sort column.
	 * @param {string} sortDir - Direction of the sort.
	 * @return {Promise<*>}
	 */
	sortMainList(sortBy, sortDir) {
		const {location, loadCampaignListAction, mainListPagination, mainListFilter} = this.props;
		const currentProjectIdFromUrl = getCurrentProjectIdFromUrl(location);
		const {pageNo, perPage} = mainListPagination;
		const oFilter = filterDataMap.output(mainListFilter);

		return this.executeAbortableAction(
			loadCampaignListAction, currentProjectIdFromUrl, oFilter, pageNo, perPage, sortBy, sortDir
		)
			.then(() => this.reloadMessageCounts());
	}

	/**
	 * Filter main list
	 * @param {Object} filter - Filter object where keys are filter field names and values are filter values.
	 * @return {Promise<*>}
	 */
	filterMainList(filter) {
		const {location, loadCampaignListAction, mainListPagination, mainListSort} = this.props;
		const currentProjectIdFromUrl = getCurrentProjectIdFromUrl(location);
		const {perPage} = mainListPagination;
		const {sortBy, sortDir} = mainListSort;
		const oFilter = filterDataMap.output(filter);

		return this.executeAbortableAction(
			loadCampaignListAction, currentProjectIdFromUrl, oFilter, 1, perPage, sortBy, sortDir
		)
		.then(() => {
			this.reloadMessageCounts();
			
			if (areAllObjectPropsEmpty(oFilter, true, false)) {
				if (this.mainListFilterRef) {
					this.mainListFilterRef.close()
						.then(() => this.setState({limitToAvailableSpace: true}));
				}
			} else {
				scrollToSelector('#main-page-table', false, 80);
			}
		});
	}

	/**
	 * Remove main list filter
	 * @return {Promise<*>}
	 */
	removeMainListFilter() {
		return this.filterMainList(null);
	}

	/**
	 * Check if main list filter is applied
	 * @return {Boolean}
	 */
	isMainListFilterEmpty() {
		return areAllObjectPropsEmpty(this.getProp('mainListFilter'), true, false);
	}
	
	/**
	 * Reloads message counts by dispatching a custom event
	 * @note Event is handled by CampaignCustomerMessageCount components which is the only way of updating their values.
	 */
	reloadMessageCounts() {
		const reloadMessageCountsEvent = new CustomEvent('onReloadMessageCounts');
		window.dispatchEvent(reloadMessageCountsEvent);
	}


	// Router methods ---------------------------------------------------------------------------------------------------
	/**
	 * Get router 'to' value of the pages 'item' sub-url
	 * @note Item sub-url uses '/item' router path and 'id' as router path param ('/item/:id') on top of to the router
	 * path of the page (see 'options.routerPath').
	 *
	 * @param {string} id - Item ID.
	 * @return {string}
	 */
	getItemRedirectTo(id) { return `${this.getRouterPathUrl()}/${id}`; }
	
	
	// Dialog methods ---------------------------------------------------------------------------------------------------
	/**
	 * Open create main list item dialog
	 */
	openCreateItemDialog() {
		const {
			openDialogAction, closeDialogAction, createCampaignItemAction, addSuccessMessageAction, showErrorMessageAction,
			createMessageDefinitionItemAction, deleteCampaignItemAction,
		} = this.props;
		
		const dialogGUIID = openDialogAction('', CreateItemDialog, {
			onSave: data => {
				const {projectList} = this.props;
				const project = find(projectList, {id: data.projectId});
				const isDynamic = (data?.type === CAMPAIGN_TYPE.DYNAMIC);
				const defaultTimezone = getString(project, 'defaultTimezone');

				// Creating dynamic campaigns require creation of a single message definition whitch in turn requires a 
				// default timezone. If timezone is not set for the project, do not create the dynamic campaign.
				if (isDynamic && !defaultTimezone) {
					showErrorMessageAction(
						this.t('no_default_timezone_desc', 'errors.campaign'),
						undefined, undefined, undefined, undefined, undefined,
						true, undefined, {title: this.t('no_default_timezone_title', 'errors.campaign')}
					);
					return;
				}
				
				// Create item (campaign)
				this.executeAbortableAction(createCampaignItemAction, data, project)
					.then(
						/**
						 * @param {CampaignItemDataObject} createdCampaign
						 * @return {Promise<void>}
						 */
						async createdCampaign => {
							if (!isset(createdCampaign)) return;

							if (isDynamic) {
								// Create a message definition for the newly created dynamic campaign
								/** @type {MessageDefinitionDataObject} */
								const createdMessageDefinition = await this.executeAbortableAction(
									createMessageDefinitionItemAction, 
									createdCampaign.id, 
									project, 
									false, 
									MESSAGE_DEFINITION_TYPE.TEMPLATE
								);
								// If it fails, delete the newly created dynamic campaign because dynamic campaigns need a 
								// message definition
								if (!isset(createdMessageDefinition)) {
									const deleteCampaign = await this.executeAbortableAction(
										deleteCampaignItemAction, createdCampaign.id
									);
									// If deleting fails, display an error message instructing the user to delete the dynamic 
									// campaign manually since it is invalid and cannot be fixed (no way to create the required 
									// message definition)
									if (!isset(deleteCampaign)) {
										showErrorMessageAction(this.t('dynamic_delete_md_fail_error_msg', 'errors.campaign'));
									}
									return;
								}
							}

							closeDialogAction(dialogGUIID);
							this.redirectToItem(createdCampaign.id);
							addSuccessMessageAction(this.t('create_success_msg'));
						});
			}
		}, {
			id: 'create-item-dialog',
			className: 'bordered-title',
			closeOnEscape: true,
			closeOnClickOutside: false,
			hideCloseBtn: false,
			maxWidth: 500
		});
		this.setOption(
			'dialogsToCloseOnUnmount',
			trimArray([...this.getOption('dialogsToCloseOnUnmount'), dialogGUIID], 'left')
		);
	}
	
	/**
	 * Open a dialog for creating new campaigns using a simplified wizard interface
	 * @type {CampaignMessageContentType} messageType - Type of message to create campaign for.
	 */
	openCampaignWizardDialog(messageType) {
		const {
			projectList, location, openDialogAction, createCampaignItemAction, createMessageDefinitionItemAction,
			updateMessageDefinitionItemAction, addSuccessMessageAction, closeDialogAction, showErrorMessageAction, 
		} = this.props;
		const currentProject = getCurrentProjectFromUrl(projectList, location);

		const dialogGUIID = openDialogAction('', CampaignWizardDialog, {
			messageType,
			project: currentProject,
			/** @param {CampaignWizardDataObject} data */
			onSave: data => {
				const project = find(this.getProp('projectList'), {id: data.projectId});
				const defaultTimezone = getString(project, 'defaultTimezone');

				// Check if default timezone is set for the project for which a new message definition should be created and
				// do not create it if it doesn't
				if (!defaultTimezone) {
					showErrorMessageAction(
						this.t('no_default_timezone_desc', 'errors.campaign'),
						undefined, undefined, undefined, undefined, undefined,
						true, undefined, {title: this.t('no_default_timezone_title', 'errors.campaign')}
					);

					// Do not create a new message definition if the project does not have a default timezone
					return;
				}
				
				// Create campaign
				this.executeAbortableAction(createCampaignItemAction, data, project)
					.then(
						/**
						 * @param {CampaignItemDataObject} createdCampaign
						 * @return {Promise<void>}
						 */
						async createdCampaign => {
							if (!isset(createdCampaign)) return;

							// Create a message definition for the newly created campaign
							/** @type {MessageDefinitionDataObject} */
							const createdMessageDefinition = await this.executeAbortableAction(
								createMessageDefinitionItemAction, createdCampaign.id, project, true
							);
							if (!isset(createdMessageDefinition)) return;

							// Update message definition with basic data and add channels
							/** @type {MessageDefinitionDataObject} */
							const updatedMessageDefinition = await this.executeAbortableAction(
								updateMessageDefinitionItemAction, {
									...createdMessageDefinition,
									startDate: data.startDate,
									channels: data.channels,
								}
							);
							if (!isset(updatedMessageDefinition)) return;

							closeDialogAction(dialogGUIID);

							// Redirect to the newly created campaign
							this.redirectTo(
								`${rtrimChar(appConfig.routerPath, '/')}/project/${data.projectId}/campaign/` +
								`${createdCampaign.id}/#wizard`
							);

							// Open edit popup for the newly created messaged definition in wizard mode and open recipients tab
							waitingFunction(() => {
								const mdElem = document.getElementById(`md-${updatedMessageDefinition.id}`);
								if (!!mdElem) {
									mdElem.click();
									return true;
								}
							}, 1, 60000)
								.then(() => waitingFunction(() => {
									const tabToNavigate = (
										updatedMessageDefinition.type === MESSAGE_DEFINITION_TYPE.REGULAR ?
											'RecipientsTab' : 'ContentTab'
									);
									const tabButtonElem = document.querySelector(
										`#message-definition-popup .popup-tab.${tabToNavigate}`
									);
									if (!!tabButtonElem) {
										tabButtonElem.click();
										return true;
									}
								}, 1, 60000));

							addSuccessMessageAction(this.t(
								'create_success_msg', 'AppsSection.DefaultApp.projectPages.CampaignPage'
							));
						});
			},
		}, {
			id: 'campaign-wizard-dialog',
			className: 'bordered-title',
			closeOnEscape: true,
			closeOnClickOutside: false,
			hideCloseBtn: false,
			maxWidth: 650
		});
		this.setOption(
			'dialogsToCloseOnUnmount',
			trimArray([...this.getOption('dialogsToCloseOnUnmount'), dialogGUIID], 'left')
		);
	}


	// Render methods ---------------------------------------------------------------------------------------------------
	/**
	 * Render data table actions cell
	 * @param {CampaignListItemDataObject} row - Data table row.
	 * @return {JSX.Element}
	 */
	renderActions(row) {
		const {location} = this.props;
		const currentProjectIdFromUrl = getCurrentProjectIdFromUrl(location);
		const canEdit = (
			!!currentProjectIdFromUrl ? 
				Acl.checkPermission(Acl, currentProjectIdFromUrl, ['CAMPAIGN_WRITE', 'CAMPAIGN_PUBLISH']) : 
				[]
		);
		
		return (
			<div className="actions">
				<Tooltip
					tag="div"
					title={
						[CAMPAIGN_STATUS.IN_PREPARATION, CAMPAIGN_STATUS.ACTIVE].includes(row.status) && canEdit ?
							this.t('edit_tooltip') :
							this.t('view_tooltip')
					}
					size="small"
					position="top-center"
					arrow={true}
					interactive={false}
				>
					<Button
						className="action-btn no-border-radius"
						displayStyle={BUTTON_STYLE.ACTION}
						displayType={BUTTON_DISPLAY_TYPE.NONE}
						icon={
							[CAMPAIGN_STATUS.IN_PREPARATION, CAMPAIGN_STATUS.ACTIVE].includes(row.status) && canEdit ? 
								icon_font_edit_symbol :
								icon_font_info_circle_symbol
						}
						onClick={() => this.redirectToItem(row.id)}
					/>
				</Tooltip>
			</div>
		);
	}

	/**
	 * Render page title
	 * @description This method specifies how page title will be rendered if page title should be rendered. It does not
	 * determine if page title should be rendered.
	 * @return {JSX.Element}
	 */
	renderPageTitle() {
		// Do not render title if project does not exist
		if (!this.projectExists()) return null;
		
		const {location, projectList, singleProject} = this.props;
		const {title} = this.state;
		/** @type {ProjectListItemDataObject} */
		const currentProject = getCurrentProjectFromUrl(projectList, location);
		const canEdit = (
			!!currentProject ? Acl.checkPermission(Acl, currentProject.id, ['CAMPAIGN_WRITE', 'CAMPAIGN_PUBLISH']) : false
		);

		return (
			<h1 className={`page-title with-actions ${!canEdit ? 'reload-only' : ''}`}>
				<div className="content">
					{singleProject ?
						<Label content={(title ? this.translate(title, this.titlePathPrefix) : '')} />
						:
						<Breadcrumbs
							includeHome={false}
							items={[
								{
									to: this.getProjectTo(),
									label: getString(currentProject, 'name')
								},
								{
									label: (title ? this.translate(title, this.titlePathPrefix) : ''),
								},
							]}
						/>
					}
				</div>
				<div className="actions">
					<div className="action-button">
						<Tooltip
							tag="div"
							title={this.t('Reload data', 'general')}
							size="small"
							position="top-center"
							arrow={true}
							interactive={false}
						>
							<Button
								big={true}
								icon="refresh"
								displayType={BUTTON_DISPLAY_TYPE.TRANSPARENT}
								displayStyle={BUTTON_STYLE.SUBTLE}
								onClick={this.reloadMainList}
							/>
						</Tooltip>
					</div>

					{canEdit ?
						<>
							<div className="action-button xs-flex-1">
								<Tooltip
									tag="div"
									title={this.t('create_new_tooltip')}
									size="small"
									position="top-center"
									arrow={true}
									interactive={false}
								>
									<span onClick={() => this.openCreateItemDialog()} className="button action solid big">
										<Label icon={icon_font_create_symbol} content={this.t('create_new')}/>
									</span>
								</Tooltip>
							</div>

							<div className="action-button main-button">
								<Tooltip
									tag="div"
									title={this.t('quick_create_new_tooltip')}
									size="small"
									position="top-center"
									arrow={true}
									interactive={false}
								>
									<AdvancedDropdown
										label={
											<Button
												className="no-margin d-block"
												big={true}
												icon="rocket"
												label={this.t('quick_create_new')}
												displayType={BUTTON_DISPLAY_TYPE.SOLID}
												displayStyle={BUTTON_STYLE.ACTION}
											/>
										}
										boundRect={
											document.querySelector('.page-title.with-actions') ?
												document.querySelector('.layout-content').getBoundingClientRect() :
												undefined
										}
										defaultPosition={ADVANCED_DROPDOWN_POSITION.LEFT}
									>
										{CAMPAIGN_MESSAGE_CONTENT_TYPES.map(p =>
											<div key={p} className="dropdown-item"
												onClick={() => this.openCampaignWizardDialog(p)}>
												<Icon {...CAMPAIGN_MESSAGE_CONTENT_TYPE_ICONS[p]} />
												<Label content={this.t(p, 'constants.messageType')}/>
											</div>
										)}
									</AdvancedDropdown>
								</Tooltip>
							</div>
						</>
						: null
					}
				</div>
			</h1>
		);
	}

	render() {
		const {
			mainList, mainListPagination, mainListSort, mainListFilter, mainSidebarShrank, toggleMainSidebarSizeAction
		} = this.props;
		const {limitToAvailableSpace} = this.state;

		return this.renderLayout((
			<div id={this.getDomId()} className={`${this.getOption('domPrefix')}`}>
				{!this.projectExists() ? this.renderMissingProject() :
					<>
						{
							this.hasTranslation('page_short_description') && this.t('page_short_description') ?
								<div className="simple-page-description">
									<Label content={this.t('page_short_description')} supportHtml={true} />
								</div>
								: null
						}

						<SimpleStaticSearch
							styleName="compact"
							className="main-search rows-2"
							defaultCollapse={true}
							layout={SIMPLE_STATIC_SEARCH_LAYOUT.STACKED}
							buttonProps={{
								displayStyle: BUTTON_STYLE.DEFAULT
							}}
							options={[
								new SimpleStaticSearchOptionObject(
									'name',
									this.t('nameField')
								),
								new SimpleStaticSearchOptionObject(
									'campaignStatus',
									this.t('statusField'),
									SIMPLE_STATIC_SEARCH_DISPLAY_TYPE.SELECT,
									{
										isClearable: true,
										options: CAMPAIGN_STATUSES.map(i => ({label: this.tt(i, 'status'), value: i}))
									}
								),
								new SimpleStaticSearchOptionObject(
									'startDate',
									this.t('startDateField'),
									SIMPLE_STATIC_SEARCH_DISPLAY_TYPE.DATE,
									{
										valueFormat: STANDARD_DATE_TIME_FORMAT.ISO_DATE_TIME_S
									}
								),
								new SimpleStaticSearchOptionObject(
									'endDate',
									this.t('endDateField'),
									SIMPLE_STATIC_SEARCH_DISPLAY_TYPE.DATE,
									{
										valueFormat: STANDARD_DATE_TIME_FORMAT.ISO_DATE_TIME_S
									}
								),
							]}
							value={mainListFilter}
							title={(<Label icon="search" content={this.t('Search', 'general')} />)}
							applied={!this.isMainListFilterEmpty()}
							enableToolbar={true}
							enableResetButton={false}
							onChange={this.filterMainList}
							onRemove={this.removeMainListFilter}
							onToggle={visible => this.setState({limitToAvailableSpace: visible})}
							ref={node => { this.mainListFilterRef = node; }}
						/>

						<DataTable
							id="main-page-table"
							//className="standard sticky-last-column"
							className="standard"
							responsive={true}
							responsiveBreakpointName="bp-xl"
							limitToAvailableSpace={limitToAvailableSpace && !this.getProp('isMobileBreakpoint')}
							highlightOnHover={true}
							columns={[
								{
									name: 'status',
									label: this.t('statusField'),
									dataType: DATA_TABLE_CELL_TYPE.ANY,
									dataTypeOptions: new DataTableCellAnyTypeOptionsDataObject({
										content: row => (
											<div className="no-wrap">
												<Label
													icon={CAMPAIGN_TYPE_ICONS[row.type]}
													element="span"
													elementProps={{
														className: `no-wrap tag highlight`, 
														style: {
															width: '2em', 
															textAlign: 'center',
															visibility: (row.type === CAMPAIGN_TYPE.DYNAMIC ? 'visible' : 'hidden'),
														},
													}}
												/>
												<Label
													element="span"
													elementProps={{className: `no-wrap tag campaign-status-${row.status}`}}
													content={this.tt(row.status, 'status')}
												/>
											</div>
										),
									}),
									width: 1,
								},
								{
									name: 'name',
									label: this.t('nameField'),
									dataType: DATA_TABLE_CELL_TYPE.TEMPLATE,
									dataTypeOptions: new DataTableCellTemplateTypeOptionsDataObject({
										template: '<strong>[{$id}]</strong> {$name}',
										supportHtml: true,
									}),
								},
								{
									name: 'description',
									label: this.t('descriptionField'),
									dataType: DATA_TABLE_CELL_TYPE.ANY,
									dataTypeOptions: new DataTableCellAnyTypeOptionsDataObject({
										content: row => <Label
											content={
												getString(row, 'description').length > 0 ?
													getString(row, 'description').substring(0, 200) +
													(getString(row, 'description').length > 200 ? ' ...' : '')
													:
													'—'
											}
										/>
									}),
								},
								{
									name: 'customer-messages-count-header',
									label: this.t('customerMessagesCount'),
									dataType: DATA_TABLE_CELL_TYPE.ANY,
									dataTypeOptions: new DataTableCellAnyTypeOptionsDataObject({
										content: row => (
											<CampaignCustomerMessageCount key={row.id} campaignId={row.id} />
										),
									}),
								},
								{
									name: 'startDate',
									label: this.t('startDateField'),
									dataType: DATA_TABLE_CELL_TYPE.DATE,
									dataTypeOptions: new DataTableCellDateTypeOptionsDataObject({
										outputFormat: getAppLocaleDateFormat(LOCALE_DATE_FORMAT_NAME.STANDARD),
										whiteSpace: 'nowrap',
									}),
									defaultValue: '—',
									width: 1,
								},
								{
									name: 'endDate',
									label: this.t('endDateField'),
									dataType: DATA_TABLE_CELL_TYPE.DATE,
									dataTypeOptions: new DataTableCellDateTypeOptionsDataObject({
										outputFormat: getAppLocaleDateFormat(LOCALE_DATE_FORMAT_NAME.STANDARD),
										whiteSpace: 'nowrap',
									}),
									defaultValue: '—',
									width: 1,
								},

								// {
								// 	dataType: DATA_TABLE_CELL_TYPE.ANY,
								// 	dataTypeOptions: {
								// 		standardWrapper: false,
								// 		content: this.renderActions,
								// 	},
								// 	stopPropagation: true,
								// 	width: 1,
								// }
							]}
							onRowClick={data => this.redirectToItem(data.id)}
							data={mainList}
							paginationType={PAGINATION_TYPE.DYNAMIC}
							onSortByColumn={this.sortMainList}
							onPaginationClick={this.loadMainListPage}
							{...mainListPagination}
							{...mainListSort}
						/>
					</>
				}
			</div>
		), undefined, undefined, {
			app: appConfig,
			mainSidebarShrank,
			toggleMainSidebarSizeAction,
		});
	}
}

export * from "./config";
export default connect(mapStateToProps, allActions)(CampaignPage);